import * as Notifications from 'expo-notifications';
import { useRootNavigationState, useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { registerForPushNotificationsAsync, saveDeviceToken } from '@/lib/notifications';
import { playPop } from '@/lib/sounds';

type PushData = {
  agreementId?: string;
  screen?: string;
  tab?: string;
  offerId?: string;
  requestId?: string;
};

function resolveTarget(data: PushData): { pathname: string; params?: Record<string, string> } | null {
  if (data.agreementId && data.screen === 'chat') {
    return { pathname: '/(app)/agreements/messages/[id]', params: { id: data.agreementId } };
  }
  if (data.agreementId) {
    return { pathname: '/(app)/agreements/[id]', params: { id: data.agreementId } };
  }
  if (data.offerId) {
    return { pathname: '/(app)/offers/[id]', params: { id: data.offerId } };
  }
  if (data.requestId) {
    return { pathname: '/(app)/requests/[id]', params: { id: data.requestId } };
  }
  if (data.screen === 'circle') {
    return data.tab === 'feed'
      ? { pathname: '/(app)/circle', params: { tab: 'feed' } }
      : { pathname: '/(app)/circle' };
  }
  if (data.screen === 'profile') {
    return { pathname: '/(app)/profile' };
  }
  return null;
}

/**
 * Εγγράφει τη συσκευή για push notifications (μόλις υπάρχει χρήστης) και
 * ανοίγει τη σχετική συμφωνία όταν ο χρήστης πατήσει ένα notification —
 * τόσο σε warm tap όσο και σε cold start. No-op στο web.
 *
 * Σε cold start, η πλοήγηση περιμένει να είναι έτοιμος τόσο ο root navigator
 * όσο και το προφίλ (loading === false) πριν κάνει router.push — αλλιώς το
 * push μπορεί να χαθεί σιωπηλά και ο χρήστης μένει κολλημένος στο spinner.
 */
export function usePushNotifications() {
  const { user } = useAuth();
  const { loading: profileLoading } = useProfile();
  const router = useRouter();
  const navState = useRootNavigationState();
  const handledRef = useRef<string | null>(null);
  const pendingRef = useRef<Notifications.NotificationResponse | null>(null);

  // Άδεια + token, αποθήκευση στη βάση. (Το register είναι ήδη no-op στο web.)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void (async () => {
      const token = await registerForPushNotificationsAsync();
      if (token && !cancelled) await saveDeviceToken(user.id, token);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Καταγραφή tap notification (cold start ή warm) — αποθηκεύεται και εκτελείται
  // μόλις είναι έτοιμος ο navigator.
  useEffect(() => {
    if (Platform.OS === 'web') return;

    function onResponse(response: Notifications.NotificationResponse | null) {
      if (!response) return;
      const requestId = response.notification.request.identifier;
      if (handledRef.current === requestId) return;
      pendingRef.current = response;
    }

    void Notifications.getLastNotificationResponseAsync().then(onResponse);
    const sub = Notifications.addNotificationResponseReceivedListener(onResponse);
    const recvSub = Notifications.addNotificationReceivedListener(() => playPop());
    return () => {
      sub.remove();
      recvSub.remove();
    };
  }, []);

  // Εκτελεί την εκκρεμή πλοήγηση μόλις είναι πραγματικά έτοιμα navigator + προφίλ.
  // Ελέγχει σε κάθε render (όχι μόνο μία φορά) ώστε να μην χαθεί tap που έγινε
  // πριν προλάβει να «ανοίξει» ο navigator.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!navState?.key) return;
    if (profileLoading) return;
    const response = pendingRef.current;
    if (!response) return;
    const requestId = response.notification.request.identifier;
    if (handledRef.current === requestId) return;

    const target = resolveTarget(response.notification.request.content.data as PushData);
    if (!target) {
      handledRef.current = requestId;
      pendingRef.current = null;
      return;
    }

    handledRef.current = requestId;
    pendingRef.current = null;
    router.push(target as never);
  });
}
